import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import Icon from './Icon';
import { createPortal } from 'react-dom';
import './SplitButton.css';

const GAP = 8; // gap-2 between the button and its menu
const EDGE = 16; // the menu stays at least this far inside the viewport

/**
 * Split Button — a primary action with a caret that opens a Context Menu of
 * related actions. Figma 1212-1072 (component 1212:1153); first use is the
 * Document view footer, "Verify and save" with Correct and Can’t verify.
 *
 * `options` is [{ label, icon, onSelect }], `icon` a Material Icons name. The
 * menu renders in a portal and positions itself against the WHOLE button, gap-2
 * below and right-aligned, flipping above when there is no room.
 *
 * `locked` blocks both halves and the menu but keeps them focusable
 * (aria-disabled, not disabled). `primaryLocked` blocks only the main half, for
 * when the default action cannot run but the menu's alternatives still can.
 *
 * While the menu is open it OWNS Escape, in the capture phase, for the same
 * reason RowActionsMenu does: hosts listen on `document` too, and one keypress
 * must not close the menu AND the modal behind it.
 */
function SplitButton({
  label, options, onPrimary, locked = false, primaryLocked = false, caretLabel = 'More options',
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const rootRef = useRef(null);
  const caretRef = useRef(null);
  const menuRef = useRef(null);
  const focusIndex = useRef(null); // which option to focus once the menu is placed
  const menuId = `split-button-menu-${useId()}`;
  const mainLocked = locked || primaryLocked;

  const items = () => [...menuRef.current.querySelectorAll('[role="menuitem"]')];

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    setPos(null);
    if (returnFocus) caretRef.current?.focus();
  }, []);

  const place = useCallback(() => {
    if (!rootRef.current || !menuRef.current) return;
    const r = rootRef.current.getBoundingClientRect();
    const m = menuRef.current.getBoundingClientRect();
    let top = r.bottom + GAP;
    if (top + m.height > window.innerHeight - EDGE) top = r.top - GAP - m.height;
    const left = Math.min(Math.max(EDGE, r.right - m.width), window.innerWidth - EDGE - m.width);
    setPos({ top: Math.max(EDGE, top), left });
  }, []);

  const openMenu = (index = 0) => {
    if (locked) return;
    focusIndex.current = index;
    setOpen(true);
  };

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  // A hidden element cannot take focus, so this waits for the placed menu.
  useEffect(() => {
    if (!open || !pos || focusIndex.current === null) return;
    const list = items();
    list[(focusIndex.current + list.length) % list.length]?.focus();
    focusIndex.current = null;
  }, [open, pos]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close();
    };
    const onOutside = (e) => {
      if (!menuRef.current?.contains(e.target) && !rootRef.current?.contains(e.target)) close(false);
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onOutside);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onOutside);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, close, place]);

  const onCaretKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); openMenu(0); }
    if (e.key === 'ArrowUp') { e.preventDefault(); openMenu(-1); }
  };

  const onMenuKeyDown = (e) => {
    const list = items();
    const i = list.indexOf(document.activeElement);
    const go = (n) => { e.preventDefault(); list[(n + list.length) % list.length].focus(); };
    if (e.key === 'ArrowDown') go(i + 1);
    else if (e.key === 'ArrowUp') go(i - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(-1);
    else if (e.key === 'Tab') close(false);
  };

  return (
    <>
      <div
        ref={rootRef}
        className={`split-button${open ? ' is-open' : ''}${locked ? ' is-locked' : ''}`}
      >
        <button
          type="button"
          className={`split-button-seg split-button-main${primaryLocked ? ' is-locked' : ''}`}
          aria-disabled={mainLocked || undefined}
          onClick={() => { if (!mainLocked) onPrimary(); }}
        >
          <span className="split-button-label">{label}</span>
        </button>
        <span className="split-button-divider" aria-hidden="true" />
        <button
          type="button"
          ref={caretRef}
          className="split-button-seg split-button-caret"
          aria-label={caretLabel}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          aria-disabled={locked || undefined}
          onClick={() => (open ? close() : openMenu(0))}
          onKeyDown={onCaretKeyDown}
        >
          <Icon name="expand_more" />
        </button>
      </div>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            className="split-button-menu"
            role="menu"
            aria-label={caretLabel}
            style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, visibility: 'hidden' }}
            onKeyDown={onMenuKeyDown}
          >
            {options.map((option) => (
              <button
                key={option.label}
                type="button"
                role="menuitem"
                className="split-button-item"
                onClick={() => { close(); option.onSelect(); }}
              >
                {option.icon && <Icon name={option.icon} />}
                <span>{option.label}</span>
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

export default SplitButton;
